import { LoadingOverlay, Stack, TextInput } from '@mantine/core';
import useCreateStaticDataset from '@/hooks/api/datasets/static/useCreateStaticDataset.ts';
import useCreateStaticDirectoryDataset from '@/hooks/api/datasets/static/useCreateStaticDirectoryDataset.ts';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import FileItem from '@/pages/AutomationPage/components/ManualRunModal/components/FileItem.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import { RelativeFile } from '@/types/common.ts';

type props = {
  opened: boolean;
  onClose: () => void;
  datasetName: string;
  setDatasetName: (name: string) => void;
  files: RelativeFile[];
  setFiles: (f: RelativeFile[]) => void;
};

const AddStaticDatasetModal = ({
  opened,
  onClose,
  files,
  setFiles,
  datasetName,
  setDatasetName,
}: props) => {
  const { mutate: addStaticDataset, isPending: isPendingFile } = useCreateStaticDataset({
    onSuccess: onClose,
  });
  const { mutate: addStaticDirectoryDataset, isPending: isPendingDirectory } =
    useCreateStaticDirectoryDataset({ onSuccess: onClose });

  const isPending = isPendingFile || isPendingDirectory;

  const isSingleFile = files.length === 1;

  return (
    <StyledModal opened={opened} onClose={onClose} title="Add data source">
      <Stack gap="sm" style={{ position: 'relative' }}>
        <LoadingOverlay visible={isPending} zIndex={1000} />

        <TextInput
          label="Data Source Name"
          placeholder="Data source name"
          size="sm"
          value={datasetName}
          onChange={(e) => setDatasetName(e.target.value)}
          autoFocus={true}
          data-autofocus
          required={true}
          readOnly={isPending}
        />
        {files.map((f, idx) => (
          <FileItem
            key={`${f.path}-${idx}`}
            name={f.path}
            size={f.file.size}
            onDelete={() => {
              const newFiles = [...files];
              newFiles.splice(idx, 1);
              setFiles(newFiles);
            }}
          />
        ))}
      </Stack>
      <ModalConfirmationButtons
        confirmLoading={isPending}
        closeOnConfirm={false}
        onClose={onClose}
        onConfirm={() => {
          if (isSingleFile) {
            addStaticDataset({ name: datasetName, file: files[0].file });
          } else {
            addStaticDirectoryDataset({ name: datasetName, files: [...files] });
          }
        }}
      />
    </StyledModal>
  );
};

export default AddStaticDatasetModal;
